import * as React from "react";
import { get } from "lodash";
import { Link } from "react-router-dom";
// UI Frameworks
import { Form, Typography, Button as AntButton, Spin, List, Row, Col, Radio } from "antd";
import { FormComponentProps } from "antd/lib/form";
// Hooks and utilities
import UserStore from "state/user";
import addCommas from "helpers/index";
import useWallet from "hooks/useWallet";
import { observer, inject } from "mobx-react";
// Services
import { isFloat } from "helpers/index";
import { persianNumber } from "helpers/persian";
import BankAccountService, { AccountModel } from "services/http/endpoints/bank-accounts";
// Shared components
import Button from "components/Button";
import Alert from "components/Alert";
import Modal from "components/Modal";
import Maybe from "components/Maybe";
import InputNumber from "components/InputNumber";
// Local components
import Main from "screens/Dashboard/components/Main";
// Styles
import styles from "./index.module.scss";

const { Text, Paragraph } = Typography;

interface IProps extends FormComponentProps {
	user: UserStore;
}

const Deposit: React.FC<IProps> = props => {
	const { fundWallet } = useWallet();
	const user = props.user ? props.user.getUser : {};
	const { getFieldDecorator, getFieldValue, setFieldsValue } = props.form;
	const [showAmountInfo, setShowAmountInfo] = React.useState(false);
	const [rial, setRial] = React.useState(false);
	const [loading, setLoading] = React.useState(false);
	const [showModal, setShowModal] = React.useState(false);
	const [finnotech, setFinnotech] = React.useState(false);
	const [isFetching, setFetching] = React.useState(true);
	const [bankAccounts, setBankAccounts] = React.useState([] as AccountModel[]);

	const fetchData = async () => {
		try {
			setLoading(true);
			BankAccountService.makeOrderBy("createdDate", false);
			BankAccountService.makeFilters("type", "EQ", "CARD");
			BankAccountService.makeFilters("status", "EQ", "CONFIRMED");
			const result = await BankAccountService.search(BankAccountService.getSearchFilter());
			setLoading(false);
			setBankAccounts(result.result);
			setFetching(false);
		} catch (error) {
			setLoading(false);
		}
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	React.useEffect(() => {
		setFinnotech(props.user.baazarConfigs.ENABLE_FINNOTECH === "true");
		// get bank accounts
		if (isFetching && finnotech) {
			fetchData();
		}
	});

	/**
	 *
	 * @param e
	 */
	function handleSubmit(e: React.FormEvent): void {
		e.preventDefault();
		props.form.validateFields((err, values) => {
			if (!err && (props.user.baazarConfigs.ENABLE_FINNOTECH === "false" || bankAccounts.length > 0)) {
				setShowAmountInfo(false);
				handleChargeConfirm(values.amount);
			}
		});
	}

	/**
	 * Validate amount
	 * @param callback
	 */
	function handleAmountValidation(_: any, value: number, callback: any) {
		if (!value) {
			callback("لطفا مقدار را وارد کنید.");
		} else if (isFloat(value)) {
			callback("لطفا مقدار اعشاری وارد نکنید.");
		} else if (value < 1000 || value > 10000000000) {
			callback(".مقدار وارد شده خارج از بازه (10,000,000,000 - 1,000) ریال می باشد");
		} else {
			callback();
		}
	}

	/**
	 * charge amount
	 * @param amount
	 */
	const handleChargeConfirm = (amount: number) => {
		setShowModal(true);
	};

	async function handleOk() {
		try {
			setLoading(true);
			await fundWallet(getFieldValue("amount"));
			setLoading(false);
		} catch (error) {
			setLoading(false);
			setShowModal(false);
			throw error;
		}
	}

	function handleCancel() {
		setShowModal(false);
	}

	function handleUpdateAmount(value: number | undefined): void {
		const val = Number(persianNumber(value));

		if (val && val < 10000000000 && val > 1000) {
			setShowAmountInfo(true);
		} else {
			setShowAmountInfo(false);
		}
	}

	return (
		<Main title="شارژ حساب کاربری">
			<div className="card">
				{props.user.aanLogin ? (
					<>
						<Alert message="شارژ حساب فقط از طریق نسخه وب و موبایل امکان پذیر است." type="warning" />
						<Paragraph className={styles["aan-alert"]} strong copyable>
							https://web.baazar.ir
						</Paragraph>
					</>
				) : (
					<>
						{get(user, "usernameType") === "EMAIL" && (
							<Radio.Group
								onChange={e => setRial(e.target.value)}
								value={rial}
								className={styles["payment-type"]}
							>
								<Radio value={false}>پرداخت ارزی</Radio>
								<Radio value={true}>پرداخت ریالی</Radio>
							</Radio.Group>
						)}
						{!props.user.aanLogin && !rial && get(user, "usernameType") === "EMAIL" && (
							<div
								dangerouslySetInnerHTML={{
									__html:
										props.user.baazarConfigs.INTERNATIONAL_PARTNERS_LIST || "در حال بارگذاری...",
								}}
							/>
						)}
						{!props.user.aanLogin && (get(user, "usernameType") !== "EMAIL" || rial) && (
							<React.Fragment>
								{loading && (
									<div style={{ textAlign: "center" }}>
										<Spin size="large" />
									</div>
								)}
								<Maybe condition={!loading && finnotech && bankAccounts.length === 0}>
									<div className={styles["no-bank-account"]}>
										<Alert
											type="warning"
											message="شما هیچ حساب بانکی تأیید شده ای برای پرداخت ندارید."
										/>
										<Link to="/dashboard/bank/accounts">ثبت حساب بانکی</Link>
									</div>
								</Maybe>
								<Maybe condition={!finnotech || bankAccounts.length > 0}>
									<Form onSubmit={handleSubmit}>
										<Form.Item label="مقدار (ریال) :">
											{getFieldDecorator("amount", {
												rules: [
													{
														transform: value => persianNumber(value),
													},
													{
														validator: handleAmountValidation,
													},
												],
											})(
												<InputNumber
													className={styles["input-number"]}
													formatter={(value: any) =>
														persianNumber(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
													}
													parser={(value: any) =>
														persianNumber(value).replace(/\$\s?|(,*)/g, "")
													}
													onChange={handleUpdateAmount}
												/>,
											)}
											{showAmountInfo && (
												<Alert
													message={
														<p>
															شما قصد پرداخت{" "}
															<b>{addCommas(getFieldValue("amount"))} ریال</b> را دارید.
														</p>
													}
													type="info"
												/>
											)}
										</Form.Item>
										{props.user.baazarConfigs.BUY_PRICES && (
											<Row gutter={[12, 12]}>
												{props.user.baazarConfigs.BUY_PRICES.split(",").map(
													(price: string, key: number) => (
														<Col key={key} md={8} xs={24}>
															<AntButton
																type="primary"
																onClick={() => setFieldsValue({ amount: price })}
																block
															>
																{addCommas(price)} ریال
															</AntButton>
														</Col>
													),
												)}
											</Row>
										)}
										{finnotech && (
											<List
												bordered
												style={{ marginTop: 10 }}
												header={<Text>برای پرداخت حتما از کارت های زیر استفاده کنید.</Text>}
												itemLayout="horizontal"
												dataSource={bankAccounts}
												renderItem={(item: any) => (
													<List.Item>
														<List.Item.Meta
															title={<Text>{item.name}</Text>}
															description={
																<span className="ltr">
																	{item.value.match(/\d{4}/g).join("-")}
																</span>
															}
														/>
													</List.Item>
												)}
											/>
										)}
										<Form.Item className="button-center">
											<Button
												htmlType="submit"
												disabled={finnotech && bankAccounts.length === 0}
												info
											>
												تایید و پرداخت
											</Button>
										</Form.Item>
										<Modal
											title="شارژ حساب"
											visible={showModal}
											onCancel={handleCancel}
											footer={[
												<AntButton key="back" type="primary" onClick={handleCancel}>
													لغو
												</AntButton>,
												<AntButton
													key="submit"
													type="primary"
													loading={loading}
													onClick={handleOk}
												>
													پرداخت
												</AntButton>,
											]}
										>
											<Text>
												شما قصد پرداخت <b>{addCommas(getFieldValue("amount"))}</b> ریال را
												دارید.
											</Text>
										</Modal>
									</Form>
								</Maybe>
							</React.Fragment>
						)}
					</>
				)}
			</div>
		</Main>
	);
};

export default Form.create({ name: "Deposit" })(inject("user")(observer(Deposit)));
